import React, { useState, useEffect, useContext, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import alarm from "../../assets/audio/alarm.mp3";
import AuthContext from "../../context/AuthContext";
import api from "../../services/AxiosConnection";
import { Vortex } from "react-loader-spinner";
import { useNotification } from "../../context/NotificationContext";
import { RxCross2 } from "react-icons/rx";

const AlarmsDialog = ({ alarms }) => {
  const [open, setOpen] = useState(false);
  const [currentAlarmIndex, setCurrentAlarmIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const { triggerNotification } = useNotification();
  const audioContextRef = useRef(null);
  const audioBufferRef = useRef(null);
  const sourceRef = useRef(null);
  const { selectedSite } = useContext(AuthContext);
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    audioContextRef.current = new AudioContext();
    
    fetch(alarm)
      .then(response => response.arrayBuffer())
      .then(buffer => audioContextRef.current.decodeAudioData(buffer))
      .then(decodedBuffer => {
        audioBufferRef.current = decodedBuffer;
        setIsAudioLoaded(true);
      })
      .catch(error => {
        console.error("Error loading alarm sound:", error);
      });

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  const playAlarm = () => {
    if (!audioContextRef.current || !audioBufferRef.current) return;
    
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {}
    }
    try {
      sourceRef.current = audioContextRef.current.createBufferSource();
      sourceRef.current.buffer = audioBufferRef.current;
      sourceRef.current.loop = true;
      sourceRef.current.connect(audioContextRef.current.destination);
      sourceRef.current.start();
    } catch (e) {
      console.error("Error playing alarm sound:", e);
    }
  };

  // Stop alarm sound
  const stopAlarm = () => {
    if (sourceRef.current) {
      try {
        sourceRef.current.stop();
      } catch (e) {}
      sourceRef.current = null;
    }
  };

  useEffect(() => {
    if (alarms.length > 0) {
      setOpen(true);
      setCurrentAlarmIndex(0);
    } else {
      setOpen(false);
    }
  }, [alarms.length]);

  useEffect(() => {
    if (open && isAudioLoaded) {
      playAlarm();
      return () => {
        stopAlarm();
      };
    } else {
      stopAlarm();
    }
  }, [open, isAudioLoaded]);

  const handleAcknowledge = async () => {
    const currentAlarm = alarms[currentAlarmIndex];
    if (!currentAlarm) return;

    setLoading(true);

    try {
      const response = await api.put(
        "/simple-view/updateTemperatureAlertAcknowledgedStatus",
        {
          siteDb: selectedSite,
          alertIds: [currentAlarm.ID],
          acknowledgedBy: user.username,
        }
      );

      if (response.status === 200) {
        triggerNotification(`Alarm Acknowledged Successfully!`, "success");
        if (currentAlarmIndex < alarms.length - 1) {
          setCurrentAlarmIndex(currentAlarmIndex + 1);
        } else {
          setOpen(false);
        }
      }
    } catch (error) {
      triggerNotification(`Unable to Acknowledge the Alarm!`, "error");
      console.error("Error acknowledging alarm:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = () => {
    if (currentAlarmIndex < alarms.length - 1) {
      setCurrentAlarmIndex(currentAlarmIndex + 1);
    } else {
      setOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const getColor = (side) => {
    switch (side) {
      case "Ts1":
      case "Ts2":
      case "Ts3":
      case "Ts4":
      case "Ts5":
      case "Ts6":
        return "bg-red-500";
      default:
        return "";
    }
  };

  // Function to determine side display
  const getSideDisplay = (side) => {
    if (["Ts1", "Ts3", "Ts5"].includes(side)) return "Left";
    if (["Ts2", "Ts4", "Ts6"].includes(side)) return "Right";
    return side || "N/A";
  };

  const currentAlarm = alarms[currentAlarmIndex];
  if (!currentAlarm) return null;

  return (
    <div>
      <style>{`
        .alarm-wheel:not(.bg-red-500) {
          background: linear-gradient(90deg, #a69885 0%, #c2b6a3 50%, #a69885 100%);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        }
        .alarm-wheel-inner:not(.bg-red-500) {
          background: linear-gradient(90deg, #a69885 0%, #c2b6a3 50%, #a69885 100%);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1);
        }
        .alarm-brake-disc:not(.bg-red-500) {
          background: linear-gradient(90deg, #b0b0b0 0%, #d1d1d1 50%, #b0b0b0 100%);
        }
        .alarm-brake-slot {
          background: #6b6b6b;
        }
        .alarm-axle {
          background: linear-gradient(180deg, #f0f0f0 0%, #d5d5d5 50%, #f0f0f0 100%);
          box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
        }
        .alarm-axle-connector:not(.bg-red-500) {
          background: linear-gradient(180deg, #d5d5d5 0%, #c0c0c0 50%, #d5d5d5 100%);
        }
        .alarm-axle-highlight:not(.bg-red-500) {
          background: transparent;
        }
        .alarm-hub {
          background: linear-gradient(90deg, #808080 0%, #606060 100%);
        }
      `}</style>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alarms-dialog-title"
        aria-describedby="alarms-dialog-description"
        scroll="paper"
        fullWidth
        maxWidth="md"
        sx={{ borderRadius: "8px" }}
      >
        <div className="flex justify-between items-center bg-blue-900 px-6 py-4">
          <DialogTitle
            id="alarms-dialog-title"
            className="font-extrabold text-white"
            sx={{ padding: 0 }}
          >
            NOVIUS HABD Alarm {currentAlarmIndex + 1} of {alarms.length}
          </DialogTitle>
          <button
            onClick={handleClose}
            className="text-white hover:text-red-400 text-xl"
            aria-label="Close Alarm Dialog"
          >
            <RxCross2 />
          </button>
        </div>

        <DialogContent dividers className="bg-gray-100">
          <DialogContentText id="alarms-dialog-description" component="div">
            <div className="flex flex-row space-x-12">
              <div className="w-1/2">
                <div className="relative w-full max-w-md h-48 flex items-center justify-center">
                  <div className="absolute left-4 flex items-center z-20">
                    <div
                      className={`alarm-wheel h-40 w-6 flex items-center justify-start relative rounded-l-md ${
                        currentAlarm.Side === "Ts3"
                          ? getColor(currentAlarm.Side)
                          : ""
                      }`}
                    >
                      <div
                        className={`alarm-wheel-inner h-48 w-3 ml-3 absolute left-0 rounded-full ${
                          currentAlarm.Side === "Ts3"
                            ? getColor(currentAlarm.Side)
                            : ""
                        }`}
                      ></div>
                      <div
                        className={`alarm-brake-disc h-32 w-3 rounded-l-sm absolute left-12 flex items-center justify-center z-20 ${
                          currentAlarm.Side === "Ts5"
                            ? getColor(currentAlarm.Side)
                            : ""
                        }`}
                      >
                        <div className="flex flex-col space-y-2 absolute">
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                        </div>
                      </div>
                      <div className="alarm-hub h-12 w-1.5 rounded-l-sm absolute left-6 z-0"></div>
                    </div>
                  </div>
                  <div
                    className={`absolute left-12 top-1/2 -translate-y-1/2 alarm-axle-connector h-9 w-6 z-10`}
                  ></div>
                  <div className="relative alarm-axle h-8 w-full max-w-md z-0">
                    <div
                      className={`absolute left-0 alarm-axle-highlight h-8 w-6 z-10 ${
                        currentAlarm.Side === "Ts1"
                          ? getColor(currentAlarm.Side)
                          : ""
                      }`}
                    ></div>
                    <div
                      className={`absolute right-0 alarm-axle-highlight h-8 w-6 z-10 ${
                        currentAlarm.Side === "Ts2"
                          ? getColor(currentAlarm.Side)
                          : ""
                      }`}
                    ></div>
                  </div>
                  <div
                    className={`absolute right-12 top-1/2 -translate-y-1/2 alarm-axle-connector h-9 w-6 z-10`}
                  ></div>
                  <div className="absolute right-4 flex items-center z-20">
                    <div
                      className={`alarm-wheel h-40 w-6 flex items-center justify-end relative rounded-r-md ${
                        currentAlarm.Side === "Ts4"
                          ? getColor(currentAlarm.Side)
                          : ""
                      }`}
                    >
                      <div
                        className={`alarm-wheel-inner h-48 w-3 rounded-full mr-3 absolute right-0 ${
                          currentAlarm.Side === "Ts4"
                            ? getColor(currentAlarm.Side)
                            : ""
                        }`}
                      ></div>
                      <div
                        className={`alarm-brake-disc h-32 w-3 rounded-r-sm absolute right-12 flex items-center justify-center ${
                          currentAlarm.Side === "Ts6"
                            ? getColor(currentAlarm.Side)
                            : ""
                        }`}
                      >
                        <div className="flex flex-col space-y-2 absolute">
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                          <div className="alarm-brake-slot h-3 w-1.5"></div>
                        </div>
                      </div>
                      <div className="alarm-hub h-12 w-1.5 rounded-r-sm absolute right-6"></div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Alarm Details */}
              <div className="w-1/2 space-y-4">
                <h3 className="font-bold text-lg">
                  Axle No: {currentAlarm.AxelNo}
                </h3>
                <div className="space-y-2">
                  <p className="text-sm">
                    <strong>Coach Position:</strong>{" "}
                    {currentAlarm.CoachPosition || "N/A"}
                  </p>
                  <p className="text-sm">
                    <strong>Coach No:</strong> {currentAlarm.CoachNo || "N/A"}
                  </p>
                  <p className="text-sm">
                    <strong>Side:</strong> {getSideDisplay(currentAlarm.Side)}
                  </p>
                  <p className="text-sm">
                    <strong>Temperature:</strong> {currentAlarm.Temperature} °C
                  </p>
                  <p className="text-sm">
                    <strong>Acknowledged By:</strong>{" "}
                    {currentAlarm.AcknowledgedBy || "Not Acknowledged Yet."}
                  </p>
                </div>
              </div>
            </div>
          </DialogContentText>
        </DialogContent>

        <DialogActions className="flex justify-center py-4 space-x-4">
          <Button
            onClick={handleAcknowledge}
            disabled={loading}
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            {loading ? (
              <Vortex
                visible={true}
                height="20"
                width="20"
                ariaLabel="vortex-loading"
                colors={["white", "white", "white", "white", "white", "white"]}
              />
            ) : (
              "Acknowledge"
            )}
          </Button>
          <Button
            onClick={handleIgnore}
            sx={{
              backgroundColor: "#6b7280",
              color: "white",
              padding: "10px 20px",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: "#4b5563",
              },
            }}
          >
            Ignore
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AlarmsDialog;